import { useEffect, useState } from "react";
import { X, Download, Loader2, FileText, ImageOff } from "lucide-react";
import { adminApi, Uploads } from "@/lib/api";

/**
 * Inline document viewer modal for admin KYC review.
 *
 * Fetches the binary via adminApi (with Bearer token) and renders it as
 * an object URL — works for PDF, JPG, PNG, WEBP. Falls back to a
 * download-only state for other content types.
 *
 * Props:
 *   files: [{ id, kind, filename }]
 *   startIndex: number (default 0)
 *   onClose: () => void
 */
export default function DocViewer({ files = [], startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const current = files[idx];

  useEffect(() => {
    let revoked = false;
    let nextUrl = null;
    const fetchFile = async () => {
      if (!current) return;
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      setContentType(null);
      try {
        const resp = await adminApi.get(`/uploads/application-doc/${current.id}`, {
          responseType: "blob",
        });
        const ct = resp.headers?.["content-type"] || "application/octet-stream";
        nextUrl = window.URL.createObjectURL(resp.data);
        if (!revoked) {
          setBlobUrl(nextUrl);
          setContentType(ct);
        }
      } catch (e) {
        if (!revoked) setError(e?.response?.data?.detail || "Could not load file.");
      } finally {
        if (!revoked) setLoading(false);
      }
    };
    fetchFile();
    return () => {
      revoked = true;
      if (nextUrl) window.URL.revokeObjectURL(nextUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight" && idx < files.length - 1) setIdx(idx + 1);
      if (e.key === "ArrowLeft" && idx > 0) setIdx(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, files.length, onClose]);

  if (!current) return null;

  const isImage = (contentType || "").startsWith("image/");
  const isPdf = contentType === "application/pdf";

  const triggerDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = current.filename || `${current.id}.bin`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div
      data-testid="doc-viewer-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex flex-col"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-5 md:px-8 py-3 border-b border-white/10 bg-[#04102B]/80">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">
            {current.kind} · {idx + 1} of {files.length}
          </div>
          <div className="text-sm text-white/90 truncate" data-testid="doc-viewer-filename">
            {current.filename || current.id}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="doc-viewer-prev"
            disabled={idx === 0}
            onClick={() => setIdx(idx - 1)}
            className="px-3 py-1.5 rounded-full border border-white/15 text-xs text-white/80 disabled:opacity-30"
          >
            ← Prev
          </button>
          <button
            data-testid="doc-viewer-next"
            disabled={idx >= files.length - 1}
            onClick={() => setIdx(idx + 1)}
            className="px-3 py-1.5 rounded-full border border-white/15 text-xs text-white/80 disabled:opacity-30"
          >
            Next →
          </button>
          <button
            data-testid="doc-viewer-download"
            onClick={triggerDownload}
            disabled={!blobUrl}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#0A1F44] text-xs font-semibold disabled:opacity-40"
          >
            <Download size={12} /> Download
          </button>
          <button
            data-testid="doc-viewer-close"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 grid place-items-center rounded-full border border-white/15 hover:bg-white/10 text-white/85"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto grid place-items-center p-4 md:p-8">
        {loading && (
          <div className="flex items-center gap-2 text-white/70">
            <Loader2 size={18} className="animate-spin text-[#F0D788]" /> Loading file…
          </div>
        )}
        {!loading && error && (
          <div className="text-center text-white/70 max-w-md">
            <ImageOff size={32} className="mx-auto mb-3 text-red-300" />
            <div className="text-sm">{error}</div>
          </div>
        )}
        {!loading && !error && blobUrl && isImage && (
          <img
            data-testid="doc-viewer-image"
            src={blobUrl}
            alt={current.filename || current.id}
            className="max-w-full max-h-[80vh] rounded-lg shadow-2xl"
          />
        )}
        {!loading && !error && blobUrl && isPdf && (
          <iframe
            data-testid="doc-viewer-pdf"
            src={blobUrl}
            title={current.filename || current.id}
            className="w-full h-[80vh] rounded-lg bg-white shadow-2xl"
          />
        )}
        {!loading && !error && blobUrl && !isImage && !isPdf && (
          <div className="text-center text-white/70 max-w-md">
            <FileText size={36} className="mx-auto mb-3 text-[#F0D788]" />
            <div className="text-sm mb-2">
              Preview not available for <code className="text-white">{contentType}</code>.
            </div>
            <button
              onClick={triggerDownload}
              className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] text-xs font-semibold"
            >
              <Download size={12} /> Download {current.filename || "file"}
            </button>
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {files.length > 1 && (
        <div className="border-t border-white/10 bg-[#04102B]/80 px-3 py-2 overflow-x-auto">
          <div className="flex gap-2">
            {files.map((f, i) => (
              <button
                key={f.id}
                data-testid={`doc-viewer-thumb-${i}`}
                onClick={() => setIdx(i)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] border whitespace-nowrap transition ${
                  i === idx
                    ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37] font-semibold"
                    : "border-white/15 text-white/70 hover:border-[#D4AF37]/60"
                }`}
              >
                {f.kind} · {f.filename?.slice(0, 24) || f.id.slice(0, 8)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Re-export Uploads.fileUrl helper consumers can use without re-importing
export const fileUrlFor = (id) => Uploads.fileUrl(id);
